import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Video } from "lucide-react";
import { useLanding } from "@/context/LandingContext";
import { SEMINAR } from "@/seminars";
import { HeroCanvas } from "./HeroCanvas";
import { SessionPicker } from "./SessionPicker";
import { NotifyForm } from "./RegistrationClosed";
import { Reveal } from "./Reveal";

const EASE = [0.2, 0.8, 0.2, 1];
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.09, delayChildren: 0.55 } } };
const fadeUp = { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } } };
const CN_NUMERALS = ["零", "一", "两", "三", "四", "五", "六", "七", "八", "九"];

// Chip template placeholders: {count} sessions, {en} / {zh} = dates per language (built from the live session list).
function heroChip(template, events, lang) {
  const dates = (language) => events.filter((e) => e.language === language).map((e) => e.short[lang]).join(lang === "zh" ? "及" : " & ");
  const count = lang === "zh" ? CN_NUMERALS[events.length] || String(events.length) : String(events.length);
  return template.replace("{count}", count).replace("{en}", dates("English")).replace("{zh}", dates("Chinese"));
}

function HeroTitle({ lines }) {
  return (
    <h1 data-testid="hero-title">
      {lines.map((line, index) => (
        <span key={`${line}-${index}`} className={`line-mask ${index === 1 ? "gold-line" : ""}`}>
          <motion.span initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.12 + index * 0.16, ease: EASE }}>
            {line}
          </motion.span>
        </span>
      ))}
    </h1>
  );
}

function QuickForm() {
  const { t, setQuickEmail, setProgressive, setRegisterOpen } = useLanding();
  const [value, setValue] = useState("");
  const inputRef = useRef(null);

  if (SEMINAR.registrationClosed) {
    return (
      <div className="hero-notify" data-testid="hero-notify">
        <p className="hero-notify-note" data-testid="hero-closed-note">{t.closed.badge} · {t.closed.title}</p>
        <NotifyForm variant="hero" sfx="-hero" />
      </div>
    );
  }

  const onSubmit = (event) => {
    event.preventDefault();
    if (!inputRef.current.checkValidity()) {
      inputRef.current.reportValidity();
      return;
    }
    setQuickEmail(value.trim());
    setProgressive(true);
    setRegisterOpen(true);
  };

  return (
    <form className="hero-quick-form" onSubmit={onSubmit} noValidate data-testid="quick-form">
      <label className="sr-only" htmlFor="quickEmail">{t.hero.quickLabel}</label>
      <input ref={inputRef} id="quickEmail" name="quick_email" type="email" autoComplete="email" required placeholder={t.hero.quickPlaceholder} value={value} onChange={(e) => setValue(e.target.value)} data-testid="quick-email-input" />
      <button className="btn btn-gold" type="submit" data-testid="quick-form-submit">
        {t.hero.quickButton} <span className="arrow">→</span>
      </button>
    </form>
  );
}

function ReflectionCard() {
  const { t, setQuestionId } = useLanding();
  const [answer, setAnswer] = useState(null);
  const pick = (index) => {
    setAnswer(index);
    setQuestionId(t.reflection.questionIds?.[index] ?? (index === 1 ? "unsure" : "review"));
  };
  return (
    <Reveal className="hero-reflection" tilt delay={0.9} aria-label="Personal reflection" data-testid="reflection-card">
      <p className="reflection-kicker">{t.reflection.kicker}</p>
      <h2>{t.reflection.title}</h2>
      <div className="reflection-options">
        {t.reflection.options.map((label, index) => (
          <button key={label} type="button" className={`reflection-option ${answer === index ? "active" : ""}`} aria-pressed={answer === index} onClick={() => pick(index)} data-testid={index === 0 ? "reflection-option-yes" : "reflection-option-unsure"}>
            {label}
          </button>
        ))}
      </div>
      <p className="reflection-answer" aria-live="polite" data-testid="reflection-answer">
        {answer == null ? t.reflection.prompt : t.reflection.answers[answer]}
      </p>
      <a className={`reflection-link ${answer != null ? "visible" : ""}`} href="#register" data-testid="reflection-link">
        {t.reflection.link}
      </a>
    </Reveal>
  );
}

export function Hero() {
  const { t, lang, events } = useLanding();
  const heroRef = useRef(null);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 140]);

  return (
    <section className="hero" ref={heroRef} data-testid="hero-section">
      <motion.img
        className="hero-bg"
        src="/hero-bg.webp"
        alt=""
        aria-hidden="true"
        style={{ y }}
        initial={{ scale: 1.025 }}
        animate={{ scale: 1.075 }}
        transition={{ duration: 16, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
      />
      <div className="hero-shade" aria-hidden="true" />
      <HeroCanvas heroRef={heroRef} />
      <div className="hero-light" aria-hidden="true" />
      <div className="hero-orbit" aria-hidden="true" />
      <div className="container hero-grid">
        <motion.div className="hero-copy" key={lang} variants={stagger} initial="hidden" animate="show">
          <motion.div className="event-chip" variants={fadeUp} data-testid="hero-event-chip">
            <span className="live-dot" aria-hidden="true" />
            <span>{SEMINAR.registrationClosed ? t.closed.chip : heroChip(t.hero.chip, events, lang)}</span>
          </motion.div>
          <HeroTitle lines={t.hero.title} />
          <motion.p className="lead" variants={fadeUp}>{t.hero.lead}</motion.p>
          <motion.div className="hero-benefits" variants={fadeUp} aria-label="What you will gain">
            {t.hero.benefits.map((benefit) => (
              <div className="hero-benefit" key={benefit}>{benefit}</div>
            ))}
          </motion.div>
          <motion.div variants={fadeUp}>
            <SessionPicker className="hero-session-picker" idPrefix="hero-session" />
            <p className="hero-format-note" data-testid="hero-format-note"><Video size={14} aria-hidden="true" /> {t.hero.formatNote}</p>
          </motion.div>
          <motion.div variants={fadeUp}>
            <QuickForm />
          </motion.div>
          <motion.div variants={fadeUp}>
            <a className="hero-secondary-link" href="#learn" data-testid="hero-learn-link">{t.hero.learnLink}</a>
            <div className="micro-row" aria-label="Webinar details">
              {t.hero.micros.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
            <div className="hero-presenters">
              <span className="mini-avatars" aria-hidden="true">
                <span className="mini-avatar">{t.hero.presenterInitials}</span>
              </span>
              <span>{t.hero.presenter}</span>
            </div>
          </motion.div>
        </motion.div>
        <ReflectionCard />
      </div>
    </section>
  );
}
